import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

interface Props {
  location: string;
  path: string;
}

/** Lightweight quality signal — logs a helpful/not-helpful vote to the
 *  existing analytics_events table. No PII, no follow-up form. */
export function LocationFeedback({ location, path }: Props) {
  const [choice, setChoice] = useState<"up" | "down" | null>(null);

  const record = (v: "up" | "down") => {
    if (choice) return;
    setChoice(v);
    trackEvent("page_feedback", {
      category: "content_quality",
      action: v === "up" ? "helpful" : "not_helpful",
      label: location,
      properties: { page_path: path },
    });
  };

  return (
    <section
      aria-label="Was this page helpful?"
      className="border-t border-border bg-background px-6 py-12 lg:px-8"
    >
      <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 text-center">
        <p className="text-sm font-medium text-foreground">
          {choice
            ? "Thanks, feedback recorded."
            : `Was this ${location} page helpful?`}
        </p>
        <div className="flex gap-3" role="group" aria-label="Rate this page">
          <button
            type="button"
            onClick={() => record("up")}
            disabled={choice !== null}
            aria-pressed={choice === "up"}
            aria-label="Yes, this page was helpful"
            className={`inline-flex min-h-11 min-w-11 items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${
              choice === "up"
                ? "border-accent bg-accent text-accent-foreground"
                : "border-border bg-background hover:border-accent"
            } disabled:cursor-not-allowed disabled:opacity-70`}
          >
            <ThumbsUp className="size-4" aria-hidden="true" />
            Yes
          </button>
          <button
            type="button"
            onClick={() => record("down")}
            disabled={choice !== null}
            aria-pressed={choice === "down"}
            aria-label="No, this page was not helpful"
            className={`inline-flex min-h-11 min-w-11 items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${
              choice === "down"
                ? "border-accent bg-accent text-accent-foreground"
                : "border-border bg-background hover:border-accent"
            } disabled:cursor-not-allowed disabled:opacity-70`}
          >
            <ThumbsDown className="size-4" aria-hidden="true" />
            No
          </button>
        </div>
      </div>
    </section>
  );
}
